import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateLearningSpaceDialog } from '@/features/learning-spaces/create-learning-space-dialog';
import { useLearningSpaces } from '@/features/learning-spaces/hooks';
import { useHasCapability } from '@/lib/capabilities';

const PAGE_SIZE = 20;

/**
 * Learning Spaces list (Phase G-6). `learning_spaces:read` is held by
 * every organization role (docs/15), so this page renders for every
 * role; only `learning_spaces:manage` gates the create action.
 */
export function LearningSpacesPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, isError } = useLearningSpaces(page, PAGE_SIZE);
  const canCreate = useHasCapability('learning_spaces:manage');

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Ruang Belajar</h1>
          <p className="text-sm text-muted-foreground">Daftar ruang belajar di organisasi ini.</p>
        </div>
        {canCreate && (
          <Button
            type="button"
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            Tambah Ruang Belajar
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat daftar ruang belajar.</AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && data !== undefined && data.items.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Belum ada ruang belajar.
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && data !== undefined && data.items.length > 0 && (
        <>
          <ul className="flex flex-col gap-2">
            {data.items.map((learningSpace) => (
              <li key={learningSpace.id}>
                <Link to={`/learning-spaces/${learningSpace.id}`}>
                  <Card className="transition-colors hover:bg-secondary/50">
                    <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <span className="truncate text-sm font-medium">{learningSpace.name}</span>
                      <Badge variant="secondary" className="capitalize">
                        {learningSpace.status.toLowerCase()}
                      </Badge>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>

          {data.total_pages > 1 && (
            <div className="flex items-center justify-between gap-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => {
                  setPage((current) => Math.max(1, current - 1));
                }}
              >
                Sebelumnya
              </Button>
              <span className="text-sm text-muted-foreground">
                Halaman {data.page} dari {data.total_pages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= data.total_pages}
                onClick={() => {
                  setPage((current) => Math.min(data.total_pages, current + 1));
                }}
              >
                Berikutnya
              </Button>
            </div>
          )}
        </>
      )}

      <CreateLearningSpaceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          setPage(1);
        }}
      />
    </div>
  );
}
